import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
import moment from 'moment';
import { useCallback } from 'react';

import { type TaskPreviewItemData } from '@app/components/LeadCard/data';

import { resolveTaskTypeIcon } from './lib/resolveTaskTypeIcon';

import type { FC, KeyboardEvent } from 'react';

import './TaskPreviewItem.scss';

interface TaskPreviewItemProps {
  task: TaskPreviewItemData;
  compact?: boolean;
}

const formatTaskRange = ({
  startsAt,
  endsAt,
}: Pick<TaskPreviewItemData, 'startsAt' | 'endsAt'>): string => {
  const start = moment(startsAt);
  const end = moment(endsAt);

  const isStartValid = start.isValid();
  const isEndValid = end.isValid();

  if (!isStartValid && !isEndValid) return '';

  const getFormat = (date: moment.Moment): string =>
    date.format('HH:mm') === '00:00' ? 'DD.MM.YY' : 'DD.MM.YY HH:mm';

  if (isStartValid && !isEndValid) {
    return start.format(getFormat(start));
  }

  if (!isStartValid && isEndValid) {
    return end.format(getFormat(end));
  }

  const isAllDayTask =
    start.isSame(end, 'day') &&
    start.format('HH:mm') === '00:00' &&
    end.format('HH:mm') === '23:59';

  if (isAllDayTask) {
    return start.format('DD.MM.YY');
  }

  return `${start.format(getFormat(start))} - ${end.format(getFormat(end))}`;
};

export const TaskPreviewItem: FC<TaskPreviewItemProps> = ({
  compact = false,
  task,
}) => {
  const {
    onOpen,
    status,
    assigneeName,
    taskTypeName,
    description,
    taskTypeIconId,
    taskTypeColor,
  } = task;
  const isClickable = onOpen !== undefined;
  const taskTypeIcon = resolveTaskTypeIcon(taskTypeIconId);

  const keyDownHandler = useCallback(
    ({ key, preventDefault }: KeyboardEvent<HTMLElement>) => {
      if (!onOpen) return;
      if (key !== 'Enter' && key !== ' ') return;

      preventDefault();
      onOpen();
    },
    [onOpen]
  );

  return (
    <article
      className={cn('TaskPreviewItem', `TaskPreviewItem--${status}`, {
        'TaskPreviewItem--compact': compact,
        'TaskPreviewItem--clickable': isClickable,
      })}
      onClick={onOpen}
      onKeyDown={keyDownHandler}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="TaskPreviewItem__content">
        <div className="TaskPreviewItem__header">
          <span className="TaskPreviewItem__type">
            {taskTypeIcon && (
              <FontAwesomeIcon
                className="TaskPreviewItem__type-icon"
                icon={taskTypeIcon}
                style={{ color: taskTypeColor || undefined }}
              />
            )}
            <span className="TaskPreviewItem__type-name">{taskTypeName}</span>
          </span>
          <span className="TaskPreviewItem__range">
            {formatTaskRange(task)}
          </span>
        </div>
        {description && (
          <div className="TaskPreviewItem__description">{description}</div>
        )}
        {assigneeName && (
          <div className="TaskPreviewItem__meta">
            <span className="TaskPreviewItem__label">Исполнитель:</span>{' '}
            {assigneeName}
          </div>
        )}
      </div>
    </article>
  );
};
